/**
 * AnnotationCanvas — a transparent drawing overlay for the shared-screen tile.
 *
 * Local pointer strokes are drawn immediately and emitted (normalised 0..1 to
 * the tile) via `onStroke`; the room broadcasts them over Daily's data channel.
 * Remote strokes arrive through the imperative `applyRemote` handle. Everything
 * is ephemeral — `clearAll` wipes the board and the room clears it when the
 * screen share stops. Collaborative: everyone can draw at once.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

// The window the canvas lives in (the app or the meeting's pop-out window): its frames and resizes drive drawing.
const viewOf = (el) => el?.ownerDocument?.defaultView || window;

export const AnnotationCanvas = forwardRef(function AnnotationCanvas(
  { active, color = '#F59E0B', width = 3, onStroke }, ref,
) {
  const canvasRef = useRef(null);
  const strokes = useRef(new Map()); // id -> { color, w, points:[{x,y}] }
  const drawingId = useRef(null);
  const rafRef = useRef(0);

  const redraw = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = viewOf(canvasRef.current).requestAnimationFrame(() => {
      rafRef.current = 0;
      const c = canvasRef.current;
      if (!c) return;
      try {
        const ctx = c.getContext('2d');
        const W = c.width;
        const H = c.height;
        const dpr = viewOf(canvasRef.current).devicePixelRatio || 1;
        ctx.clearRect(0, 0, W, H);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const s of strokes.current.values()) {
          if (!s.points.length) continue;
          ctx.strokeStyle = s.color;
          ctx.lineWidth = (s.w || 3) * dpr;
          ctx.beginPath();
          s.points.forEach((p, i) => {
            const x = p.x * W;
            const y = p.y * H;
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          });
          ctx.stroke();
        }
      } catch { /* never let a single bad frame wedge future redraws */ }
    });
  }, []);

  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = viewOf(canvasRef.current).devicePixelRatio || 1;
    c.width = Math.max(1, Math.round(rect.width * dpr));
    c.height = Math.max(1, Math.round(rect.height * dpr));
    redraw();
  }, [redraw]);

  useEffect(() => {
    resize();
    const ro = new (viewOf(canvasRef.current).ResizeObserver || ResizeObserver)(resize);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [resize]);

  useImperativeHandle(ref, () => ({
    applyRemote(msg) {
      if (!msg) return;
      if (msg.t === 'draw') {
        let s = strokes.current.get(msg.id);
        if (!s) { s = { color: msg.color, w: msg.w, points: [] }; strokes.current.set(msg.id, s); }
        if (msg.points) s.points.push(...msg.points);
        redraw();
      } else if (msg.t === 'clear') {
        strokes.current.clear();
        redraw();
      }
    },
    getState() {
      return Array.from(strokes.current.entries())
        .map(([id, s]) => ({ id, color: s.color, w: s.w, points: s.points }));
    },
    applyState(list) {
      (list || []).forEach((s) => {
        if (!strokes.current.has(s.id)) {
          strokes.current.set(s.id, { color: s.color, w: s.w, points: s.points || [] });
        }
      });
      redraw();
    },
    clearAll() { strokes.current.clear(); drawingId.current = null; redraw(); },
  }), [redraw]);

  const norm = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const onDown = (e) => {
    if (!active) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    drawingId.current = id;
    const p = norm(e);
    strokes.current.set(id, { color, w: width, points: [p] });
    onStroke?.({ t: 'draw', id, color, w: width, points: [p] });
    redraw();
  };
  const onMove = (e) => {
    if (!active || !drawingId.current) return;
    const s = strokes.current.get(drawingId.current);
    if (!s) return;
    const p = norm(e);
    s.points.push(p);
    onStroke?.({ t: 'draw', id: drawingId.current, color, w: width, points: [p] });
    redraw();
  };
  const onUp = () => { drawingId.current = null; };

  return (
    <canvas
      ref={canvasRef}
      data-testid="mm-annotation-canvas"
      className="absolute inset-0 h-full w-full"
      style={{
        touchAction: 'none',
        cursor: active ? 'crosshair' : 'default',
        pointerEvents: active ? 'auto' : 'none',
        zIndex: 5,
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    />
  );
});

export default AnnotationCanvas;
